/**
 * AI-generated code, moderated by Lukasz Zygmanski
 */
import type {ClientEntitlements} from '@nursio-link/common/authorization';
import {EntitlementsModules} from '@nursio-link/common/model';

import {isSupportedLanguage} from '@/i18nConfig';
import {UserGroup} from '@/services/graphqlHooks';

import type {
  LocalizedReleaseNoteText,
  ReleaseBuildMetadata,
  ReleaseNote,
  ReleaseNoteLanguage,
} from '../types/releaseNotes';

type ReleaseNoteFrontmatter = {
  id: string;
  title: string;
  summary: string;
  publishedAt: string;
  audiences: UserGroup[];
  entitlements?: ClientEntitlements[];
  announce?: boolean;
};

type ParsedReleaseNoteFile = {
  language: ReleaseNoteLanguage;
  frontmatter: ReleaseNoteFrontmatter;
  body: string;
};

type DraftReleaseNote = {
  id?: string;
  title?: Partial<LocalizedReleaseNoteText>;
  summary?: Partial<LocalizedReleaseNoteText>;
  publishedAt?: string;
  audiences?: UserGroup[];
  entitlements?: ClientEntitlements[];
  announce?: boolean;
  body?: Partial<LocalizedReleaseNoteText>;
};

type FrontmatterMetadata = Record<string, string | string[]>;

type FrontmatterParseState = {
  metadata: FrontmatterMetadata;
  currentListKey: string | null;
};

const releaseNoteFiles = import.meta.glob<string>(
  '../../../release-notes/**/*.md',
  {
    eager: true,
    import: 'default',
    query: '?raw',
  }
);

export const releaseBuildMetadata: ReleaseBuildMetadata = {
  buildNumber: __APP_BUILD_NUMBER__,
  buildDate: __APP_BUILD_DATE__,
  commitSha: __APP_BUILD_COMMIT_SHA__ || undefined,
};

export const releaseNotes = loadReleaseNotes(releaseNoteFiles);

function loadReleaseNotes(files: Record<string, string>): ReleaseNote[] {
  const releaseNotesById = Object.entries(files).reduce<
    Record<string, DraftReleaseNote>
  >((releaseNotesById, [path, file]) => {
    const parsedReleaseNote = parseReleaseNoteFile(path, file);

    return {
      ...releaseNotesById,
      [parsedReleaseNote.frontmatter.id]: mergeReleaseNoteDraft(
        releaseNotesById[parsedReleaseNote.frontmatter.id],
        parsedReleaseNote
      ),
    };
  }, {});

  return Object.values(releaseNotesById)
    .map(assertReleaseNoteHasDefaultLanguage)
    .sort(
      (first, second) =>
        new Date(second.publishedAt).getTime() -
        new Date(first.publishedAt).getTime()
    );
}

function mergeReleaseNoteDraft(
  currentReleaseNote: DraftReleaseNote | undefined,
  parsedReleaseNote: ParsedReleaseNoteFile
): DraftReleaseNote {
  const {body, frontmatter, language} = parsedReleaseNote;

  return {
    ...currentReleaseNote,
    id: frontmatter.id,
    announce: frontmatter.announce ?? currentReleaseNote?.announce,
    audiences: frontmatter.audiences,
    entitlements: frontmatter.entitlements ?? [],
    publishedAt: frontmatter.publishedAt,
    body: {
      ...currentReleaseNote?.body,
      [language]: body,
    },
    summary: {
      ...currentReleaseNote?.summary,
      [language]: frontmatter.summary,
    },
    title: {
      ...currentReleaseNote?.title,
      [language]: frontmatter.title,
    },
  };
}

function parseReleaseNoteFile(
  path: string,
  file: string
): ParsedReleaseNoteFile {
  const language = getLanguageFromPath(path);
  const frontmatterMatch = file.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);

  if (!frontmatterMatch) {
    throw new Error(`Release note ${path} is missing frontmatter.`);
  }

  return {
    body: frontmatterMatch[2].trim(),
    frontmatter: parseReleaseNoteFrontmatter(path, frontmatterMatch[1]),
    language,
  };
}

function parseReleaseNoteFrontmatter(
  path: string,
  frontmatter: string
): ReleaseNoteFrontmatter {
  const {metadata} = frontmatter
    .split('\n')
    .reduce<FrontmatterParseState>(parseFrontmatterLine, {
      currentListKey: null,
      metadata: {},
    });

  const id = getRequiredStringMetadata(path, metadata, 'id');
  const title = getRequiredStringMetadata(path, metadata, 'title');
  const summary = getRequiredStringMetadata(path, metadata, 'summary');
  const publishedAt = getRequiredStringMetadata(path, metadata, 'publishedAt');
  const audiences = getAudienceMetadata(path, metadata);
  const entitlements = getEntitlementMetadata(path, metadata);
  const announce = getAnnounceMetadata(path, metadata);

  return {
    announce,
    audiences,
    entitlements,
    id,
    publishedAt,
    summary,
    title,
  };
}

function parseFrontmatterLine(
  state: FrontmatterParseState,
  line: string
): FrontmatterParseState {
  const listItemMatch = line.match(/^\s*-\s+(.+)$/);

  if (state.currentListKey && listItemMatch) {
    const currentValue = state.metadata[state.currentListKey];

    return {
      ...state,
      metadata: {
        ...state.metadata,
        [state.currentListKey]: [
          ...(Array.isArray(currentValue) ? currentValue : []),
          listItemMatch[1].trim(),
        ],
      },
    };
  }

  const keyValueMatch = line.match(/^([A-Za-z]+):\s*(.*)$/);
  if (!keyValueMatch) return state;

  const [, key, value] = keyValueMatch;

  if (value === '[]') {
    return {
      currentListKey: null,
      metadata: {
        ...state.metadata,
        [key]: [],
      },
    };
  }

  if (value) {
    return {
      currentListKey: null,
      metadata: {
        ...state.metadata,
        [key]: value.trim(),
      },
    };
  }

  return {
    currentListKey: key,
    metadata: {
      ...state.metadata,
      [key]: [],
    },
  };
}

function getLanguageFromPath(path: string): ReleaseNoteLanguage {
  const language = path.match(/\/release-notes\/([^/]+)\//)?.[1];

  if (language && isSupportedLanguage(language)) {
    return language;
  }

  throw new Error(`Release note ${path} uses an unsupported language folder.`);
}

function getRequiredStringMetadata(
  path: string,
  metadata: FrontmatterMetadata,
  key: string
) {
  const value = metadata[key];

  if (typeof value !== 'string' || !value) {
    throw new Error(`Release note ${path} is missing ${key} metadata.`);
  }

  return value;
}

function getAudienceMetadata(path: string, metadata: FrontmatterMetadata) {
  const value = metadata.audiences;

  if (!Array.isArray(value)) {
    throw new Error(`Release note ${path} is missing audiences metadata.`);
  }

  return value.map(audience => {
    if (isUserGroup(audience)) return audience;

    throw new Error(
      `Release note ${path} uses unsupported audience ${audience}.`
    );
  });
}

function getEntitlementMetadata(
  path: string,
  metadata: FrontmatterMetadata
): ClientEntitlements[] | undefined {
  const value = metadata.entitlements;

  if (value === undefined) return undefined;

  if (!Array.isArray(value)) {
    throw new Error(`Release note ${path} has invalid entitlements metadata.`);
  }

  return value.map(entitlement => {
    if (isClientEntitlement(entitlement)) return entitlement;

    throw new Error(
      `Release note ${path} uses unsupported entitlement ${entitlement}.`
    );
  });
}

function getAnnounceMetadata(
  path: string,
  metadata: FrontmatterMetadata
): boolean | undefined {
  const value = metadata.announce;

  if (value === undefined) return undefined;

  if (value === 'true') return true;
  if (value === 'false') return false;

  throw new Error(
    `Release note ${path} uses unsupported announce ${
      Array.isArray(value) ? value.join(', ') : value
    }.`
  );
}

function assertReleaseNoteHasDefaultLanguage(releaseNote: DraftReleaseNote) {
  if (
    !releaseNote.id ||
    !releaseNote.publishedAt ||
    !releaseNote.audiences ||
    !hasDefaultLocalizedText(releaseNote.body) ||
    !hasDefaultLocalizedText(releaseNote.summary) ||
    !hasDefaultLocalizedText(releaseNote.title)
  ) {
    throw new Error(
      `Release note ${releaseNote.id || 'unknown'} must include an en file.`
    );
  }

  return {
    announce: releaseNote.announce || false,
    audiences: releaseNote.audiences,
    body: releaseNote.body,
    entitlements: releaseNote.entitlements || [],
    id: releaseNote.id,
    publishedAt: releaseNote.publishedAt,
    summary: releaseNote.summary,
    title: releaseNote.title,
  } satisfies ReleaseNote;
}

function hasDefaultLocalizedText(
  text?: Partial<LocalizedReleaseNoteText>
): text is LocalizedReleaseNoteText {
  return !!text?.en;
}

function isUserGroup(audience: string): audience is UserGroup {
  return Object.values(UserGroup).includes(audience as UserGroup);
}

function isClientEntitlement(value: string): value is ClientEntitlements {
  return Object.values(EntitlementsModules).includes(
    value as EntitlementsModules
  );
}
